import { useState } from "react";
import { useLogin, useIsAuthenticated } from "@refinedev/core";
import { useLocation, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTheme } from "next-themes";
import { Loader2 } from "lucide-react";
import { LiquidLogo } from "@/components/ui/liquid-logo";

interface LoginVariables {
  email: string;
  password: string;
  redirectTo?: string;
}

/**
 * Login page for admin panel
 *
 * Redirect flow:
 * 1. Незалогиненный пользователь попадает на /login?to=/some-page
 * 2. После успешного login, authProvider возвращает { redirectTo: "/some-page" }
 * 3. Refine автоматически делает redirect
 *
 * Если пользователь уже залогинен — Refine через authProvider.check() увидит
 * authenticated: true и покажет Authenticated fallback, а LoginRedirect в App.tsx
 * его не тронет, потому что он уже на /login. Поэтому здесь проверяем вручную
 * и показываем null для уже залогиненных.
 */
export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const location = useLocation();
  const { theme, resolvedTheme } = useTheme();

  const { mutate: login, isPending } = useLogin<LoginVariables>();
  const { data: authData, isLoading: isAuthLoading } = useIsAuthenticated();

  const currentTheme = resolvedTheme || theme || "dark";
  const logoSrc =
    currentTheme === "dark" ? "/logo-white.svg" : "/logo-black.svg";

  const redirectToParam = new URLSearchParams(location.search).get("to");
  const redirectTo =
    redirectToParam &&
    redirectToParam.startsWith("/") &&
    !redirectToParam.startsWith("//") &&
    !redirectToParam.startsWith("/login")
      ? redirectToParam
      : "/";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Refine обработает redirect через { redirectTo } в authProvider.login()
    login({ email, password, redirectTo });
  };

  if (isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Если уже залогинен — редирект на целевую страницу или главную
  if (authData?.authenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4 overflow-hidden">
      {/* Background Logo Decoration (Liquid Shimmer) */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none select-none">
        <LiquidLogo
          height="50vh"
          opacity={currentTheme === "dark" ? 0.15 : 0.1}
        />
      </div>

      <Card className="relative z-10 w-full max-w-md shadow-2xl bg-card border-primary/10">
        <CardHeader className="text-center pb-8">
          <div className="mx-auto flex items-center justify-center h-16">
            <img src={logoSrc} alt="Masters Club" className="h-full w-auto" />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isPending}
                required
                autoComplete="email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isPending}
                required
                autoComplete="current-password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Вход...
                </>
              ) : (
                "Войти"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
